import { ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { useNotification } from '@kyvg/vue3-notification'
import _ from 'lodash'
import StreamService from '@/services/StreamService'
import StreamStatsService from '@/services/StreamStatsService'
import IntegrationService from '@/services/IntegrationService'
import { APP_EVENTS, appEventBus } from '@/Utils/appEventBus'
import { composePlatformPushUrl, computePlatformStatus } from '@/Utils/platformStatus'

export function usePlatformList({
	stream,
	mediaPulse,
	streamPlatforms,
	regions,
	schedulerConfig,
	oauth,
	platformToggleProcessing,
	computeGroupToggleState,
	onPlatformList,
}) {
	const store = useStore()
	const router = useRouter()
	const { notify } = useNotification()
	const scopeAlive = ref(true)
	let platformRefreshInterval = null
	let onScheduleMode = null

	function isFacebookBlockedInLoop(platform) {
		return schedulerConfig.value?.mode === 'loop'
			&& platform.template === 'facebook live'
			&& !!platform.linkedServiceCreds
	}

	function checkPlatformConnectStatus(specificPlatform) {
		_.each(streamPlatforms.value, (platform, index) => {
			if (!specificPlatform || specificPlatform._id === platform._id) {
				if (isFacebookBlockedInLoop(platform)) {
					if (platform.connected || platform.enabled) {
						streamPlatforms.value[index] = {
							...platform,
							connected: false,
							enabled: false,
							tooltipId: platform.tooltipId || platform._id,
						}
					}
					return
				}
				const platformPushUrl = composePlatformPushUrl(platform)
				const connectStatus = computePlatformStatus(platformPushUrl, mediaPulse.value)
				if (platform.connected !== connectStatus && !platform.streamObject) {
					const pchanges = _.assign({}, platform, { connected: connectStatus })
					streamPlatforms.value[index] = pchanges
				}
			}
		})
		onPlatformList(streamPlatforms.value)
	}

	// Unsaved metadata edits on the current row, so a background refresh doesn't wipe them
	function pendingMetaEdits(platformId) {
		const current = _.find(streamPlatforms.value, { _id: platformId })
		const editor = current?.serviceMetaEditor
		if (!editor?.form) return {}
		const pending = {}
		_.forIn(editor.form, (value, prop) => {
			if (value !== current.serviceMeta?.[prop]) pending[prop] = value
		})
		return { pending, changes: _.pickBy(editor.changes, (changed, prop) => changed && prop in pending) }
	}

	async function normalizePlatform(platform) {
		let serviceMetaEditor = {}
		if (platform.serviceMeta) {
			const { pending, changes } = pendingMetaEdits(platform._id)
			serviceMetaEditor = {
				formProcessing: _.mapValues(platform.serviceMeta, () => false),
				form: _.assign({}, platform.serviceMeta, pending),
				changes: changes || {},
			}
		}

		let platformTemplate = await IntegrationService.getPublishPlatform(platform.template)
		platformTemplate = platformTemplate || {}

		const subChannelMeta = _.get(platform, 'serviceMeta.subChannel')
		let isFBUserTimeline = false
		if (!_.isEmpty(subChannelMeta)) {
			isFBUserTimeline = subChannelMeta.split('@')[0] === 'user'
		}

		platform = {
			...platform,
			serviceMetaEditor,
			editorName: platform.name,
			icon: platformTemplate.icon,
			config: !isFBUserTimeline
				? (platformTemplate.config || {})
				: { ...platformTemplate.config, type: 'userFBtimeline' },
			removingProcessing: false,
		}

		if (regions.value.length > 0 && platform.streamObject?.uiRegion && typeof platform.streamObject?.uiRegion === 'string') {
			platform.streamObject.uiRegion = regions.value.find((x) => x._id === platform.streamObject?.uiRegion)
		}

		if (regions.value.length > 0 && platform.streamObject?.region && typeof platform.streamObject?.region === 'string') {
			platform.streamObject.region = regions.value.find((x) => x._id === platform.streamObject?.region)
		}

		if (isFacebookBlockedInLoop(platform)) {
			platform.tooltipId = platform._id
			platform.enabled = false
			platform.connected = false
		}
		return platform
	}

	async function normalizePlatformList(platformList) {
		if (platformToggleProcessing.value) return
		try {
			const nextPlatforms = _.cloneDeep(platformList || stream.value.platforms)
			for (let i = 0; i < nextPlatforms.length; i++) {
				nextPlatforms[i] = await normalizePlatform(nextPlatforms[i])
				if (nextPlatforms[i].template === 'youtube events') {
					nextPlatforms[i].maxLengthTitle = 100
					nextPlatforms[i].maxLengthDescription = 5000
					nextPlatforms[i].showTitleLimitTracking = true

					if (nextPlatforms[i].linkedServiceCreds) {
						const authToken = await StreamService.getAccessTokenByServiceCred(nextPlatforms[i].linkedServiceCreds)
						try {
							const metadata = await IntegrationService.isYoutubeTokenValid(
								nextPlatforms[i].linkedServiceCreds,
								nextPlatforms[i].serviceMeta?.subChannel
							)
							if (_.isBoolean(metadata.isTokenValid)) {
								nextPlatforms[i].isTokenValid = metadata.isTokenValid
							}
						} catch (error) {
							nextPlatforms[i].isTokenValid = false
						}

						nextPlatforms[i].ytAccount = authToken?._id
						oauth.ensureYoutubeState(authToken?._id)
					} else {
						nextPlatforms[i].isTokenValid = true
					}

					if (!nextPlatforms[i].isTokenValid) {
						nextPlatforms[i].enabled = false
					}
				} else {
					nextPlatforms[i].showTitleLimitTracking = false
					nextPlatforms[i].maxLengthTitle = 1000
					nextPlatforms[i].maxLengthDescription = 5000
				}

				if (!_.isBoolean(nextPlatforms[i].isTokenValid)) nextPlatforms[i].isTokenValid = true

				if (nextPlatforms[i].template === 'facebook live' && nextPlatforms[i]?.linkedServiceCreds) {
					const authToken = await StreamService.getAccessTokenByServiceCred(nextPlatforms[i].linkedServiceCreds)
					nextPlatforms[i].isTokenValid = await IntegrationService.isFacebookTokenValid(nextPlatforms[i].linkedServiceCreds)
					nextPlatforms[i].fbAccount = authToken?.account
					oauth.ensureFacebookState(authToken?.account)
					if (!nextPlatforms[i].isTokenValid) {
						nextPlatforms[i].enabled = false
					}
				}
			}

			streamPlatforms.value = _.map(nextPlatforms, (item) => {
				const platform = _.find(streamPlatforms.value, (i) => i._id === item._id)
				if (
					platform
					&& Object.prototype.hasOwnProperty.call(platform, 'connected')
					&& !isFacebookBlockedInLoop(item)
				) {
					item.connected = platform.connected
				}
				return item
			})
			store.dispatch('Ui/setLoadingStatus', false)
			computeGroupToggleState()
		} catch (err) {
			await router.push({ name: 'ChannelList' })
			notify({
				group: 'error',
				title: err.error,
				text: err.message,
			})
		}

		checkPlatformConnectStatus()
	}

	async function fetchStreamPlatformList() {
		if (platformToggleProcessing.value) return
		try {
			const streamObj = await StreamService.getStream(stream.value._id)
			await normalizePlatformList(streamObj.platforms)
		} catch (e) {
			console.log('platform-list-err', e)
		}
	}

	async function reloadPlatforms() {
		await normalizePlatformList(stream.value.platforms)
	}

	async function platformRefresh() {
		if (!scopeAlive.value) return

		let shouldRefreshPlatformList = false

		if (mediaPulse.value && mediaPulse.value.push_stats) {
			stream.value.platforms.forEach((p) => {
				const isLinkedFb = p.enabled && p.template === 'facebook live' && p.linkedServiceCreds
				if (isLinkedFb) {
					const pushUrl = composePlatformPushUrl(p)
					if (!mediaPulse.value.push_stats[pushUrl]) {
						shouldRefreshPlatformList = true
					}
				}
				const isYoutubeEvents = p.enabled && p.template === 'youtube events'
				if (isYoutubeEvents) {
					const pushUrl = composePlatformPushUrl(p)
					if (!mediaPulse.value.push_stats[pushUrl]) {
						shouldRefreshPlatformList = true
					}
				}
			})
		}

		streamPlatforms.value.map(async (platform, idx) => {
			if (platform.streamObject) {
				const pulseObject = await StreamStatsService.getStreamMediaPulse(platform.streamObject.key)
				if (pulseObject && pulseObject.alive) {
					platform.connected = true
				} else platform.connected = false
				streamPlatforms.value[idx] = platform
				return platform
			}
		})

		if (shouldRefreshPlatformList && !document.hidden) {
			try {
				await fetchStreamPlatformList()
			} catch (e) { /* ignore refresh errors */ }
		}
	}

	async function bootstrap({ onScheduled, onNotScheduled, afterFirstNormalize } = {}) {
		store.dispatch('Ui/setLoadingStatus', true)
		await store.dispatch('Regions/getRegions', 'list')
		regions.value = _.cloneDeep(store.getters['Regions/getRegionsByCategory']('list'))

		if (stream.value.type === 'scheduled') {
			try {
				const cachedConfig = await store.dispatch('StreamCache/getSchedulerConfig', stream.value._id) || {}
				schedulerConfig.value = _.cloneDeep(cachedConfig)
			} catch {
				schedulerConfig.value = schedulerConfig.value || {}
			}
			onScheduleMode = async (data) => {
				if (schedulerConfig.value) schedulerConfig.value.mode = data
				if (platformToggleProcessing.value === false) await fetchStreamPlatformList()
			}
			appEventBus.on(APP_EVENTS.SCHEDULE_MODE, onScheduleMode)
			onScheduled?.()
		} else {
			onNotScheduled?.()
		}

		await normalizePlatformList(stream.value.platforms)

		await afterFirstNormalize?.()
		await fetchStreamPlatformList()

		platformRefreshInterval = setInterval(async () => {
			try {
				await platformRefresh()
			} catch (error) {
				console.log(error)
			}
		}, 5000)
	}

	function teardown() {
		scopeAlive.value = false
		clearInterval(platformRefreshInterval)
		if (onScheduleMode) {
			appEventBus.off(APP_EVENTS.SCHEDULE_MODE, onScheduleMode)
		}
	}

	watch(mediaPulse, () => {
		checkPlatformConnectStatus()
	})

	return {
		scopeAlive,
		normalizePlatformList,
		normalizePlatform,
		fetchStreamPlatformList,
		reloadPlatforms,
		checkPlatformConnectStatus,
		platformRefresh,
		bootstrap,
		teardown,
	}
}
